import { useState, useEffect } from 'react'
import { usePMOFFeature } from '@vend/business-components'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { useAppSelector } from '../../../../state/hooks'
import { selectSaleCustomer } from '../../../../state/slices/currentSale/currentSaleSlice'
import { selectTrainingModeEnabled } from '../../../../state/slices/posState/posStateSlice'
import { isValidCustomer } from '../../../../functions/sale'
import {
  useRegisterManager,
  useUserManager,
  useWebRegisterStatusManager,
} from '../../../../ng-compat/hooks'
import { fetchLSPayCustomerPaymentMethods } from '../../../../functions/payment-types/lspayPmof'
import { PERMISSIONS } from '../../../../constants'

export interface HasCustomerSavedPaymentMethodsResult {
  hasSavedMethods: boolean
  isResolved: boolean
}

/**
 * Hook to determine if the current sale's customer has saved payment methods on file.
 * Mirrors the visibility logic of LSPayPMOF component.
 */
export const useHasCustomerSavedPaymentMethods = (): HasCustomerSavedPaymentMethodsResult => {
  const [hasSavedMethods, setHasSavedMethods] = useState(false)
  const [isResolved, setIsResolved] = useState(false)

  const outlet = useRegisterManager().getActiveOutlet()
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const userManager = useUserManager()
  const validCustomer = isValidCustomer(saleCustomer)

  const featureFlagEnabled = getFeatureFromCache('enable_customer_saved_cards')
  const isTrainingMode = useAppSelector(selectTrainingModeEnabled)
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const { isEnabled: featureEnabled } = usePMOFFeature()
  const userPermissionGranted = userManager.hasPermission(
    PERMISSIONS.LSPAY_PMOF_ADD_EDIT_USE
  )

  const enabled =
    featureFlagEnabled &&
    featureEnabled &&
    userPermissionGranted &&
    !isTrainingMode &&
    webRegisterStatusManager.isOnline()

  useEffect(() => {
    let cancelled = false

    async function load() {
      let hasMethods = false
      try {
        if (enabled && validCustomer && saleCustomer?.id && outlet?.id) {
          const methods = await fetchLSPayCustomerPaymentMethods(
            saleCustomer.id,
            outlet.id
          )
          hasMethods = (methods ?? []).length > 0
        }
      } catch {
        // hasMethods stays false
      }
      if (!cancelled) {
        setHasSavedMethods(hasMethods)
        setIsResolved(true)
      }
    }

    setIsResolved(false)
    load()
    return () => {
      cancelled = true
    }
  }, [enabled, validCustomer, saleCustomer?.id, outlet?.id])

  return { hasSavedMethods, isResolved }
}
